import { useMemo, useState } from 'react';
import { Edit3, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useGoChecklist,
  useRecomputeChecklist,
} from '../hooks/use-go-checklist';
import { ItemStatusBadge, VerdictBadge } from './verdict-badge';
import { ItemOverrideDialog } from './item-override-dialog';
import type {
  GoChecklistCategory,
  GoChecklistItem,
} from '../types/go-checklist.types';

const CATEGORY_LABELS: Record<GoChecklistCategory, string> = {
  ADMIN_DOC: 'Pièces administratives',
  CERTIFICATION: 'Certifications',
  FINANCIAL: 'Capacité financière',
  LEGAL_STATUS: 'Statut juridique',
  HUMAN_CAPACITY: 'Capacité humaine',
  RSE: 'RSE / Marchés réservés',
};

const CATEGORY_ORDER: GoChecklistCategory[] = [
  'LEGAL_STATUS',
  'FINANCIAL',
  'HUMAN_CAPACITY',
  'ADMIN_DOC',
  'CERTIFICATION',
  'RSE',
];

export function GoChecklistTab({ opportunityId }: { opportunityId: number }) {
  const { data: checklist, isLoading } = useGoChecklist(opportunityId);
  const recompute = useRecomputeChecklist(opportunityId);
  const [overrideTarget, setOverrideTarget] = useState<GoChecklistItem | null>(null);

  const groupedByCategory = useMemo(() => {
    const groups: Record<GoChecklistCategory, GoChecklistItem[]> = {
      ADMIN_DOC: [],
      CERTIFICATION: [],
      FINANCIAL: [],
      LEGAL_STATUS: [],
      HUMAN_CAPACITY: [],
      RSE: [],
    };
    if (checklist) {
      for (const item of checklist.items) {
        groups[item.category].push(item);
      }
    }
    return groups;
  }, [checklist]);

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (!checklist) {
    return (
      <Card className="p-8 text-center text-muted-foreground">
        <p className="mb-4">
          Aucune checklist GO encore calculée pour cette opportunité.
        </p>
        <p className="text-sm mb-4">
          Lancez d'abord l'analyse DCE depuis l'onglet « Analyse » — la checklist sera calculée automatiquement à la fin.
        </p>
        <Button
          onClick={() => recompute.mutate()}
          disabled={recompute.isPending}
          variant="outline"
        >
          {recompute.isPending ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <RefreshCw className="h-4 w-4 mr-2" />
          )}
          Calculer maintenant
        </Button>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête : verdict + score + recalcul */}
      <Card className="p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <VerdictBadge verdict={checklist.verdict} score={checklist.verdictScore} />
            </div>
            <p className="text-sm text-muted-foreground">
              Calculée le {new Date(checklist.createdAt).toLocaleString('fr-FR')}
              {' · '}
              {checklist.triggeredBy === 'MANUAL'
                ? `recalcul manuel${checklist.triggeredByUser ? ` par ${checklist.triggeredByUser.firstName} ${checklist.triggeredByUser.lastName}` : ''}`
                : 'calcul auto'}
              {' · '}
              {checklist.items.length} items
            </p>
          </div>
          <Button
            onClick={() => recompute.mutate()}
            disabled={recompute.isPending}
            variant="outline"
          >
            {recompute.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Recalculer
          </Button>
        </div>
      </Card>

      {/* Sections par catégorie */}
      {CATEGORY_ORDER.map((cat) => {
        const items = groupedByCategory[cat];
        if (items.length === 0) return null;
        const conformCount = items.filter((i) =>
          (i.manualOverrideStatus ?? i.status) === 'CONFORM',
        ).length;
        return (
          <Card key={cat} className="p-6">
            <h3 className="text-base font-semibold mb-4 flex items-center justify-between">
              <span>{CATEGORY_LABELS[cat]}</span>
              <span className="text-sm font-normal text-muted-foreground">
                {conformCount}/{items.length} conformes
              </span>
            </h3>
            <div className="space-y-3">
              {items.map((item) => {
                const effectiveStatus = item.manualOverrideStatus ?? item.status;
                return (
                  <div
                    key={item.id}
                    className="flex items-start justify-between gap-3 pb-3 border-b last:border-0 last:pb-0"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <ItemStatusBadge
                          status={effectiveStatus}
                          isOverridden={item.manualOverride}
                        />
                        <span className="text-sm font-medium">{item.subcategory}</span>
                        {item.lot && (
                          <span className="text-xs text-muted-foreground">
                            Lot {item.lot.lotNumber}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground line-clamp-2">
                        {item.requirementText}
                      </p>
                      {item.evidenceDescription && (
                        <p className="text-xs text-muted-foreground mt-1">
                          Évidence : {item.evidenceDescription}
                        </p>
                      )}
                      {item.gapDescription && (
                        <p className="text-xs text-red-700 mt-1">
                          ⚠ {item.gapDescription}
                        </p>
                      )}
                      {item.manualOverride && item.manualOverrideReason && (
                        <p className="text-xs text-amber-700 italic mt-1">
                          Override : « {item.manualOverrideReason} »
                          {item.manualOverrideBy && (
                            <>
                              {' '}— {item.manualOverrideBy.firstName} {item.manualOverrideBy.lastName}
                            </>
                          )}
                        </p>
                      )}
                    </div>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setOverrideTarget(item)}
                      title="Override manuel"
                    >
                      <Edit3 className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          </Card>
        );
      })}

      <ItemOverrideDialog
        item={overrideTarget}
        onClose={() => setOverrideTarget(null)}
      />
    </div>
  );
}
